import React from 'react';
import { navigate } from 'gatsby';
import { useAuth } from '../context/AuthContext';

// Constants for paths and storage keys
const INTERNAL_PATHS = ['/login/', '/logout/'];
const HOME_PATH = '/';
const LOGIN_PATH = '/login';
const REDIRECT_STORAGE_KEY = 'redirectAfterLogin';

/**
 * Checks if the current path is an internal path.
 * @param {string} path - The current path.
 * @returns {boolean} - True if the path is internal, false otherwise.
 */
export const isInternalPath = path => INTERNAL_PATHS.includes(path);

const PrivateRoute = ({ children, location }) => {
  // Get the current user from the auth context
  const { user } = useAuth();

  // Destructure location props for easier access
  const { pathname, search, hash } = location;
  const fullPath = `${pathname}${search}${hash}`;

  // If user data is not yet fetched, return null (loading state)
  if (user?.state === 'NOT_FETCHED_YET') {
    return null;
  }

  // Define conditions for different redirection scenarios
  const shouldRedirectToLogin = !user && !isInternalPath(pathname);
  const shouldRedirectToHome =
    user &&
    user.isSignedIn &&
    isInternalPath(pathname) &&
    user.path !== fullPath;
  const shouldRedirectFromPublicToHome =
    user &&
    user.isSignedIn &&
    isInternalPath(pathname) &&
    (!user.path || isInternalPath(user.path));

  /**
   * Stores the current path for redirection after login
   */
  const storeRedirectPath = () => {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(REDIRECT_STORAGE_KEY, fullPath);
      }
    } catch (error) {
      console.error('Failed to store redirect path:', error);
    }
  };

  // Handle different redirection scenarios
  switch (true) {
    case shouldRedirectToLogin:
      // If user is not logged in and trying to access a protected route
      storeRedirectPath();
      navigate(LOGIN_PATH);
      return null;

    case shouldRedirectToHome:
    case shouldRedirectFromPublicToHome:
      // If user is logged in and trying to access login/logout pages
      // Or if user is logged in and on a public page they shouldn't access
      navigate(HOME_PATH);
      return null;

    default:
      // If none of the redirection conditions are met, render the children
      return <>{children}</>;
  }
};

export default PrivateRoute;
